import { isDefined } from "@clipboard-health/util-ts";
import { put } from "@src/appV2/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type TimeRange } from "@src/appV2/Shifts/Shift/types";
import {
  getExtraWorkedTime,
  getMissedPunchExtraWorkedTime,
  type MissedPunch,
} from "@src/appV2/Shifts/Shift/WorkedBreakAuthorization/utils";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

export interface SubmitShiftTimeSheetParameters {
  clockInOut: TimeRange;
  lunchInOut?: TimeRange;
  shiftId: string;
  signedBy: string;
  breakAuthorizationReason?: string;
  missedPunches?: MissedPunch[];
  actualClockInOut?: TimeRange;
  actualLunchInOut?: TimeRange;
  location?: {
    latitude: string;
    longitude: string;
  };
  timecard: {
    url: string;
    filename: string;
    fileStorageFileKey: string;
  };
  mandatoryBreakDurationInMinutes: number;
  authorizingEmployeeName?: string;
  authorizingEmployeeSignature?: string;
}

export const DEFAULT_TIMESHEET_LOCATION = {
  latitude: "12",
  longitude: "34",
};

export function useSubmitShiftTimeSheet() {
  return useMutation({
    mutationFn: async (data: SubmitShiftTimeSheetParameters) => {
      const {
        clockInOut,
        lunchInOut,
        shiftId,
        signedBy,
        breakAuthorizationReason,
        missedPunches,
        actualClockInOut,
        actualLunchInOut,
        location = DEFAULT_TIMESHEET_LOCATION,
        timecard,
        mandatoryBreakDurationInMinutes,
        authorizingEmployeeName,
        authorizingEmployeeSignature,
      } = data;

      const authorizingEmployee =
        isDefined(authorizingEmployeeName) && isDefined(authorizingEmployeeSignature)
          ? { name: authorizingEmployeeName, signature: authorizingEmployeeSignature }
          : undefined;

      // Get break extraWorkedTime
      const breakExtraWorkedTime = getExtraWorkedTime({
        mandatoryBreakDurationInMinutes,
        clockInOut,
        lunchInOut,
        breakAuthorizationReason,
        authorizingEmployee,
      });

      const missedPunchExtraWorkedTime = getMissedPunchExtraWorkedTime({
        submitClockInOut: clockInOut,
        actualClockInOut,
        submitLunchInOut: lunchInOut,
        actualLunchInOut,
        missedPunches,
      });

      const allExtraWorkedTime = [...(breakExtraWorkedTime ?? []), ...missedPunchExtraWorkedTime];
      const extraWorkedTime = allExtraWorkedTime.length > 0 ? allExtraWorkedTime : undefined;

      await put({
        url: `/v2/shifts/timecard/${shiftId}`,
        data: {
          timecard: [timecard],
          location: [location.longitude, location.latitude],
          submitClockInOut: clockInOut,
          submitLunchInOut: lunchInOut,
          digitallySignedBy: signedBy,
          extraWorkedTime,
        },
        responseSchema: z.unknown(),
      });
      return timecard;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.ERROR_SUBMITTING_SHIFT_TIME_SHEET,
    },
  });
}
